// Brands admin screen (internal_admin). A brand is the email SENDER IDENTITY used for a
// company's Console emails. Create/edit change the sender, so they're step-up-gated.
//
// ⚠️ The from_email MUST be a verified SES identity or every email silently fails — the
// form says so, since the Console can't verify SES for you.
import { useEffect, useState, useCallback } from "react";
import { App, Card, Table, Tag, Button, Space, Modal, Form, Input, Switch, Typography, Alert } from "antd";
import { ReloadOutlined, PlusOutlined } from "@ant-design/icons";
import { brandsApi, accountApi } from "../../console/consoleApi";
import { useStepUp } from "../../console/useStepUp";
import { errorMessage } from "../../console/consoleUtils";

const { Title } = Typography;

const isCancelled = (err) => err?.message === "STEP_UP_CANCELLED";

export default function ConsoleBrands() {
  const { message } = App.useApp();
  const { withStepUp, modal } = useStepUp();
  const [brands, setBrands] = useState([]);
  const [accountCount, setAccountCount] = useState({}); // brandId -> # accounts
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // brand being edited, or null = create
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [brandRes, accRes] = await Promise.all([brandsApi.list(), accountApi.listAll()]);
      setBrands(brandRes.data.data.brands);
      // Tally how many accounts point at each brand.
      const counts = {};
      for (const a of accRes.data.data.accounts) {
        if (a.brandId != null) counts[a.brandId] = (counts[a.brandId] ?? 0) + 1;
      }
      setAccountCount(counts);
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true });
    setModalOpen(true);
  };

  const openEdit = (brand) => {
    setEditing(brand);
    form.setFieldsValue(brand);
    setModalOpen(true);
  };

  const submit = async (values) => {
    setSaving(true);
    try {
      if (editing) {
        // key is immutable — send only editable fields.
        const payload = {
          displayName: values.displayName,
          fromEmail: values.fromEmail,
          isActive: values.isActive,
        };
        await withStepUp((token) => brandsApi.update(editing.id, payload, token));
        message.success("Brand updated.");
      } else {
        await withStepUp((token) => brandsApi.create(values, token));
        message.success("Brand created.");
      }
      setModalOpen(false);
      load();
    } catch (err) {
      if (!isCancelled(err)) message.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: "Key", dataIndex: "key", key: "key", render: (k) => <Tag>{k}</Tag> },
    { title: "Display name", dataIndex: "displayName", key: "displayName" },
    { title: "Sender (from_email)", dataIndex: "fromEmail", key: "fromEmail" },
    {
      title: "Accounts",
      key: "accounts",
      align: "center",
      render: (_, row) => accountCount[row.id] ?? 0,
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      align: "center",
      render: (v) => <Tag color={v ? "green" : "default"}>{v ? "active" : "inactive"}</Tag>,
    },
    {
      title: "",
      key: "actions",
      align: "right",
      render: (_, row) => (
        <Button size="small" onClick={() => openEdit(row)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Card
      title={
        <Title level={4} style={{ margin: 0 }}>
          Brands
        </Title>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New brand
          </Button>
        </Space>
      }
    >
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={brands}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
      />

      <Modal
        title={editing ? `Edit ${editing.displayName}` : "New brand"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        okText={editing ? "Save" : "Create"}
        confirmLoading={saving}
        destroyOnHidden
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title="The sender address must be verified in SES first"
          description="If from_email isn't a verified SES identity, invites and codes for this brand will silently fail to send."
        />
        <Form form={form} layout="vertical" onFinish={submit} requiredMark={false}>
          <Form.Item
            name="key"
            label="Key"
            help="Stable identifier, lowercase (e.g. verifyu, fidaro). Can't be changed later."
            rules={[{ required: true, message: "Enter a key" }]}
          >
            <Input placeholder="fidaro" disabled={!!editing} autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="displayName"
            label="Display name"
            rules={[{ required: true, message: "Enter a display name" }]}
          >
            <Input placeholder="Fidaro" autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="fromEmail"
            label="Sender address (from_email)"
            rules={[{ required: true, type: "email", message: "Enter a valid email" }]}
          >
            <Input placeholder="no-reply@fidaro.ai" autoComplete="off" />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Step-up (re-OTP) modal for create/edit */}
      {modal}
    </Card>
  );
}
